import {Component, OnInit} from '@angular/core';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {BookingService} from '../../services/booking/booking.service';
import {Booking} from '../../../core/entities/booking/booking.entity';
import {User, UserRoles} from '../../../core/entities/user/user.entity';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';

import {AuthenticationService} from '../../../core/services/authentication/authentication.service';
import {RegistrationService} from '../../../core/services/registration/registration.service';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Tokens} from '../../../core/entities/authentication/tokens.entity';
import {ToastrService} from 'ngx-toastr';
import {MetaPixelService} from '../../../core/services/analytics/meta-pixel.service';
import {AppImageComponent} from '../../../core/components/image/image';
import {CurrencyPipe} from '@angular/common';
import {Service} from '../../../core/entities/service/service.entity';
import {ServiceService} from '../../services/service/service.service';
import {TrackingService} from '../../../core/services/tracking/tracking.service';
import {TrackingEventName} from '../../../core/entities/tracking/tracking-event.entity';

@Component({
  selector: 'app-booking-create-success',
  imports: [
    TranslocoPipe,
    RouterLink,
    ReactiveFormsModule,
    AppImageComponent,
    CurrencyPipe
],
  templateUrl: './booking-create-success.component.html',
  styleUrl: './booking-create-success.component.scss'
})
export class BookingCreateSuccessComponent implements OnInit {
  public booking: Booking|null = null;
  public service: Service|null = null;
  public billingInfo: BillingInfo|null = null;
  public user: User|null = null;
  public customerEmail: string|null = null;
  public accountForm: FormGroup;
  public bookingId: number|null = null;
  private serviceId: number|null = null;
  private bookingLoadInFlight = false;
  private purchaseTracked = false;
  private paymentEventTracked = false;
  private serviceResolved = false;
  private billingInfoResolved = false;
  private queryParamsReady = false;
  public constructor(
    private readonly fb: FormBuilder,
    private readonly toastr: ToastrService,
    private readonly translocoService: TranslocoService,
    private readonly authenticationService: AuthenticationService,
    private readonly bookingService: BookingService,
    private readonly serviceService: ServiceService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly metaPixel: MetaPixelService,
    private readonly registrationService: RegistrationService,
    private readonly router: Router,
    private readonly tracking: TrackingService,
  ) {
    this.accountForm = this.fb.group({
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(6)]],
      passwordConfirm: ['', Validators.required],
    }, {
      validators: this.passwordsMatchValidator
    });
  }

  public passwordsMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    return group.get('password')?.value === group.get('passwordConfirm')?.value
      ? null : { passwordMismatch: true };
  }

  public ngOnInit(): void {
    this.user = this.authenticationService.getCurrentUser();
    this.activatedRoute.params.subscribe(params => {
      const parsed = Number(params['id']);
      this.bookingId = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      const serviceParsed = Number(params['service']);
      if (Number.isFinite(serviceParsed) && serviceParsed > 0) {
        this.serviceId = serviceParsed;
        this.loadService(serviceParsed);
      } else {
        this.serviceResolved = true;
      }
      if (this.bookingId) {
        this.loadBooking(this.bookingId);
        this.tryLoadBillingInfos();
      }
    });

    this.activatedRoute.queryParams.subscribe(queryParams => {
      this.customerEmail = queryParams['email'] || null;
      this.queryParamsReady = true;
      if (this.customerEmail === null) {
        this.toastr.error(
          this.translocoService.translate('marketplace.booking.create.success.create-account.no-email.content'),
          this.translocoService.translate('marketplace.booking.create.success.create-account.no-email.title'),
        );
        this.router.navigate(['/']);
      }
      this.tryLoadBillingInfos();
      const qpFirstname = queryParams['firstname'] || null;
      const qpLastname = queryParams['lastname'] || null;
      this.prefillNamesFromQuery(qpFirstname, qpLastname);
    });
  }


  public createAccount(): void {
    if (this.accountForm.invalid || !this.customerEmail) return;

    const { firstname, lastname, password } = this.accountForm.getRawValue();

    // Get the local timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.registrationService.register(
      this.customerEmail,
      password,
      UserRoles.customer,
      timezone,
      firstname,
      lastname,
    ).subscribe({
      next: (tokens: Tokens) => {
        this.authenticationService.saveTokens(tokens);
        this.toastr.success(
          this.translocoService.translate('marketplace.booking.create.success.create-account.form.success.content'),
          this.translocoService.translate('marketplace.booking.create.success.create-account.form.success.title')
        );
        this.user = this.authenticationService.getCurrentUser();
        this.metaPixel.trackRegistration({
          user: this.user,
          email: this.customerEmail,
          role: UserRoles.customer,
          source: 'booking_checkout',
        });
        this.trackPurchaseIfNeeded();
      },
      error: () => {
        this.toastr.error(
          this.translocoService.translate('marketplace.booking.create.success.create-account.form.error.content'),
          this.translocoService.translate('marketplace.booking.create.success.create-account.form.error.title')
        );
      }
    });
  }

  private loadService(serviceId: number): void {
    this.serviceService.getService(serviceId).subscribe({
      next: (srv) => {
        this.service = srv;
        this.serviceResolved = true;
        this.trackPaymentSuccess();
      },
      error: () => {
        this.service = null;
        this.serviceResolved = true;
        this.trackPaymentSuccess();
      }
    });
  }

  private loadBooking(bookingId: number): void {
    if (this.bookingLoadInFlight) return;
    this.bookingLoadInFlight = true;

    this.bookingService.getBooking(bookingId).subscribe({
      next: (booking) => {
        this.booking = booking;
        this.bookingLoadInFlight = false;
        this.prefillNamesFromBooking(booking);
        this.trackPurchaseIfNeeded();
      },
      error: () => {
        this.bookingLoadInFlight = false;
      }
    });
  }

  private tryLoadBillingInfos(): void {
    if (!this.bookingId) return;

    if (!this.customerEmail) {
      if (this.queryParamsReady) {
        this.billingInfoResolved = true;
        this.trackPaymentSuccess();
      }
      return;
    }
    this.bookingService.getBillingInfos(this.bookingId, this.customerEmail).subscribe({
      next: (info) => {
        this.billingInfo = info;
        this.billingInfoResolved = true;
        this.trackPaymentSuccess();
      },
      error: () => {
        this.billingInfo = null;
        this.billingInfoResolved = true;
        this.trackPaymentSuccess();
      }
    });
  }

  private prefillNamesFromBooking(booking: Booking): void {
    const firstname = booking?.customer?.firstname ?? '';
    const lastname = booking?.customer?.lastname ?? '';

    if (firstname) {
      this.accountForm.patchValue({ firstname });
      this.accountForm.get('firstname')?.disable({ emitEvent: false });
    }

    if (lastname) {
      this.accountForm.patchValue({ lastname });
      this.accountForm.get('lastname')?.disable({ emitEvent: false });
    }
  }

  private prefillNamesFromQuery(firstname: string|null, lastname: string|null): void {
    if (firstname) {
      this.accountForm.patchValue({ firstname });
      this.accountForm.get('firstname')?.disable({ emitEvent: false });
    }
    if (lastname) {
      this.accountForm.patchValue({ lastname });
      this.accountForm.get('lastname')?.disable({ emitEvent: false });
    }
  }

  private trackPurchaseIfNeeded(): void {
    if (!this.booking || this.purchaseTracked) return;

    this.metaPixel.trackPurchase({
      booking: this.booking,
      user: this.user,
      roleHint: UserRoles.customer,
      eventId: `purchase-${this.booking.id}`,
    });
    this.purchaseTracked = true;
  }

  private trackPaymentSuccess(): void {
    if (this.paymentEventTracked || !this.bookingId || !this.billingInfoResolved || !this.serviceResolved) return;

    const serviceId = this.service?.id ?? this.serviceId;
    if (!serviceId) {
      return;
    }

    const basePrice = this.billingInfo
      ? (this.billingInfo.publicPrice ?? null)
      : this.service?.publicPrice ?? null;
    const discount = this.billingInfo?.discount ?? 0;
    const amount = basePrice != null ? basePrice - discount : null;

    const flow = this.tracking.getLastFlow() ?? 'service_page';
    this.tracking.track(TrackingEventName.CHECKOUT_SUCCEEDED, {
      booking_id: this.bookingId,
      service_id: serviceId,
      service_name: this.service?.name,
      amount,
      currency: 'EUR',
      discount,
      deposit_amount: this.billingInfo?.depositAmount ?? null,
      status: this.billingInfo?.status ?? 'success',
    }, {
      gaEventName: 'checkout_succeeded',
      idempotencyKey: this.bookingId ? `checkout:${this.bookingId}:SUCCEEDED` : undefined,
      flow,
    });

    this.paymentEventTracked = true;
  }

  public get isCustomer(): boolean {
    return !!this.user && this.user.roles.includes(UserRoles.customer);
  }
}

interface BillingInfo {
  paymentDate: string|null;
  status: string;
  datetime: string;
  publicPrice: number;
  discount: number|null;
  depositAmount: number|null;
  invoiceUrl: string|null;
}
